import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  isWithinInterval,
  subDays,
  parse,
} from "date-fns";
import { is } from "src/util/is";
import { 
  convertLocalToUTC, 
  convertUTCToLocal, 
  formatUTCToLocal, 
  getCurrentUTC,
  isRazorpayTransaction,
  convertTransactionDateTime,
  convertTransactionsDateTime 
} from "./dateTimeUtils";

export const parseDateTimeString = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) {
    if (!isNaN(dateStr.getTime())) return dateStr;
    return new Date();
  }
  
  if (typeof dateStr === "string") {
    if (dateStr.includes("/")) {
      const parts = dateStr.split(" ");
      const datePart = parts[0]; 
      const timePart = parts[1] || "00:00:00"; 
      const [day, month, year] = datePart.split("/");
      const [hours, minutes, seconds = "00"] = timePart.split(":");
      const dt = new Date(year, month - 1, day, hours, minutes, seconds);
      if (!isNaN(dt.getTime())) return dt;
    }
    
    // date-fns parsing for hyphenated formats
    if (dateStr.includes("-") && dateStr.toLowerCase().match(/am|pm/)) {
      // Try yyyy-MM-dd first (TransactionView format)
      let parsed = parse(dateStr, "yyyy-MM-dd hh:mm:ss a", new Date());
      if (!isNaN(parsed.getTime())) return parsed;
      
      parsed = parse(dateStr, "yyyy-MM-dd hh:mm a", new Date());
      if (!isNaN(parsed.getTime())) return parsed;

      // Try MM-dd-yyyy (ChargerForm manual entry format)
      parsed = parse(dateStr, "MM-dd-yyyy hh:mm:ss a", new Date());
      if (!isNaN(parsed.getTime())) return parsed;

      parsed = parse(dateStr, "MM-dd-yyyy hh:mm a", new Date());
      if (!isNaN(parsed.getTime())) return parsed;
    } else if (dateStr.includes("-") && dateStr.includes(":")) {
      let parsed = parse(dateStr, "yyyy-MM-dd HH:mm:ss", new Date());
      if (!isNaN(parsed.getTime())) return parsed;

      parsed = parse(dateStr, "MM-dd-yyyy HH:mm:ss", new Date());
      if (!isNaN(parsed.getTime())) return parsed;
    } else if (dateStr.includes("-") && !dateStr.includes(":")) {
      // YYYY-MM-DD case is handled well by new Date(), but MM-DD-YYYY is not
      let parsed = parse(dateStr, "yyyy-MM-dd", new Date());
      if (!isNaN(parsed.getTime())) return parsed;

      parsed = parse(dateStr, "MM-dd-yyyy", new Date());
      if (!isNaN(parsed.getTime())) return parsed;
    }
    
    // Fallback native parse
    const nativeParsed = new Date(dateStr);
    if (!isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }
  }
  
  return new Date();
};

// ----------------------------------------------------------------------

export function fDate(date) {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate ? format(localDate, "dd MMMM yyyy") : '';
}

export function fDateTime(date, dateFormat = "dd MMM yyyy HH:mm") {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate && isValid(localDate)
    ? format(localDate, dateFormat)
    : "";
}

export function fDateTimeSuffix(date) {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate ? format(localDate, "dd/MM/yyyy hh:mm p") : '';
}

export function f(date, _format) {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate ? format(localDate, _format) : '';
}

export function fLocalTime(date) {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate && isValid(localDate)
    ? format(localDate, "MMMM dd, yyyy, hh:mm:ss")
    : "";
}

export function fToNow(date) {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate ? formatDistanceToNow(localDate, {
    addSuffix: true,
  }) : '';
}

export const onlyTime = (date) => {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate && isValid(localDate)
    ? format(localDate, "hh:mm:ss a")
    : "";
};

export const yearFirstDateTimeFormat = (date) => {
  if (!date) return '';
  // Convert UTC to local time for display
  const localDate = convertUTCToLocal(date);
  return localDate && isValid(localDate)
    ? format(localDate, "yyyy-MM-dd hh:mm:ss a")
    : "";
};

export const yearFirstDateTimeFormatUTC = (date) => {
  if (is.empty(date) || !isValid(new Date(date))) return "";
  
  // Parse the date as UTC to avoid timezone conversion
  // Extract the date part and format it properly
  const dateOnly = date.split('T')[0];
  const timePart = date.split('T')[1]?.split('.')[0] || '00:00:00';
  return `${dateOnly} ${timePart}`;
};

export const yearFirstDateFormatUTC = (date) => {
  if (is.empty(date) || !isValid(new Date(date))) return "";
  
  // Parse the date as UTC to avoid timezone conversion
  // Extract just the date part (YYYY-MM-DD) from the ISO string
  const dateOnly = date.split('T')[0];
  return dateOnly;
};

export const changeToISODateTimeString = (data) => {
  let date = data;
  if (typeof date === "object") {
    date = date.toISOString();
  } else {
    date = new Date(date).toISOString();
  }
  const withoutUTCZulu = date.replace("Z", "+00:00");
  return withoutUTCZulu;
};

export const remoteOperationsDateFormat = (data) => {
  if (!data) return "";
  
  // Convert local date/time to UTC for API submission
  const utcDate = convertLocalToUTC(data);
  
  if (!utcDate) return "";
  
  // Create Date object from UTC ISO string
  const utcDateObj = new Date(utcDate);
  
  // Format in UTC timezone (not local timezone)
  // Use UTC methods to ensure we format in UTC, not local time
  const year = utcDateObj.getUTCFullYear();
  const month = String(utcDateObj.getUTCMonth() + 1).padStart(2, "0");
  const day = String(utcDateObj.getUTCDate()).padStart(2, "0");
  const hours = String(utcDateObj.getUTCHours()).padStart(2, "0");
  const minutes = String(utcDateObj.getUTCMinutes()).padStart(2, "0");
  
  const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;
  
  return formattedDate;
};
export const withinTimeBounds = (
  dateTimeToCompare,
  dateTimeToCompareAgainst,
  limitToConsider
) => {
  const upperLimitBound = new Date(dateTimeToCompareAgainst);
  const lowerTimeBound = new Date(
    upperLimitBound.getTime() - limitToConsider * 60000
  );

  const isWithinBounds = isWithinInterval(dateTimeToCompare, {
    start: lowerTimeBound,
    end: upperLimitBound,
  });
  return isWithinBounds;
};

export const giveTimeAndUTCTimeOffset = (
  timeToCalculateAgainst = new Date()
) => {
  const timezoneOffset = format(timeToCalculateAgainst, "xxx"); //OFFSET IN FORMAT '+/- hh:mm'
  const offsetInHrs = timezoneOffset.toString().split(/-|\+/gm)[1]; // ARRAY OF OFFSET STRING ['+||-',"hh:mm"]
  const offsetDirection =
    offsetInHrs.split(":")[0] === "00"
      ? "equal"
      : timezoneOffset[0] === "+"
      ? "ahead"
      : "behind";
  return offsetDirection !== "equal" ? (
    <p style={{ color: "red" }}>
      Your current timezone is {offsetInHrs}hrs {offsetDirection}.
    </p>
  ) : (
    <p style={{ color: "green" }}>
      Your current timezone is in accordance with UTC.
    </p>
  );
};

export const dashboardDateFormatter = (date, dateFormat = "yyyy-MM-dd") => {
  return format(date, dateFormat);
};

export const timeIntervalFormatter = (endDate, intervalValue) => {
  const startDate = subDays(endDate, intervalValue);
  
  // Set end date to 23:59:59 UTC
  const endDateWithTime = new Date(endDate);
  endDateWithTime.setUTCHours(23, 59, 59, 999);
  
  return {
    start: startDate.toISOString(),
    end: endDateWithTime.toISOString(),
  };
};

export const isValidTimeInterval = ({ start, end }) => {
  if (!start || !end) return false;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;
  
  const today = new Date();
  const isStartBeforeOrEqualToEnd =
    isBefore(startDate, endDate) || isEqual(startDate, endDate);
  const bothNotAfterCurrent =
    isAfter(startDate, today) && isAfter(endDate, today);
  return isStartBeforeOrEqualToEnd && !bothNotAfterCurrent;
};

export const differenceBetween = ({ start, end }) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const difference = differenceInCalendarDays(endDate, startDate);
  return difference;
};

export const calculateBaseDateRange = ({ start, end, type }) => {
  let dateGap = differenceBetween({ start, end });
  let previousDay = subDays(new Date(start), 1);
  const baseDateRange = timeIntervalFormatter(previousDay, dateGap);


  return {
    start,
    end,
    type,
    baseStart: baseDateRange.start,
    baseEnd: baseDateRange.end,
  };
};

export const addTimeToDateString = (
  dateString,
  time = format(new Date(), "HH:mm:ss"),
  isEndDate = false
) => {
  // For end dates, always use 23:59:59
  const finalTime = isEndDate ? "23:59:59" : time;
  const updatedDateString = `${dateString} ${finalTime}`;
  return updatedDateString;
};

export const splitDateAndTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  const yyyy = date.getFullYear();
  let mm = date.getMonth() + 1; 
  let dd = date.getDate();

  if (dd < 10) dd = "0" + dd;
  if (mm < 10) mm = "0" + mm;

  return dd + "-" + mm + "-" + yyyy;
};

// ----------------------------------------------------------------------
// Transaction Date/Time Conversion Functions
// ----------------------------------------------------------------------

/**
 * Format date/time for display, converting from UTC to local time
 * @param {string} utcDateTime - UTC date/time string from backend
 * @param {string} format - Format type ('date', 'time', 'datetime', 'iso')
 * @returns {string} - Formatted local time string
 */
export const fTransactionDateTime = (utcDateTime, format = 'datetime') => {
  if (!utcDateTime) return '-';
  return formatUTCToLocal(utcDateTime, format);
};

/**
 * Format transaction date/time with year first format, converting from UTC to local
 * @param {string} utcDateTime - UTC date/time string from backend
 * @returns {string} - Formatted local time string
 */
export const fTransactionYearFirstDateTime = (utcDateTime) => {
  if (!utcDateTime) return '-';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';
  
  return format(localDate, "yyyy-MM-dd hh:mm:ss a");
};

/**
 * Convert local date/time to UTC for API submission
 * @param {Date|string} localDateTime - Local date/time
 * @returns {string} - UTC ISO string
 */
export const fLocalToUTC = (localDateTime) => {
  return convertLocalToUTC(localDateTime);
};

/**
 * Convert UTC date/time to local time for display
 * @param {string} utcDateTime - UTC ISO string from backend
 * @returns {Date} - Local date object
 */
export const fUTCToLocal = (utcDateTime) => {
  return convertUTCToLocal(utcDateTime);
};

/**
 * Convert transaction object date/time fields from UTC to local time
 * @param {Object} transaction - Transaction object
 * @returns {Object} - Transaction with converted date/time fields
 */
export const fConvertTransactionToLocal = (transaction) => {
  return convertTransactionDateTime(transaction, 'toLocal');
};

/**
 * Convert transaction object date/time fields from local to UTC time
 * @param {Object} transaction - Transaction object
 * @returns {Object} - Transaction with converted date/time fields
 */
export const fConvertTransactionToUTC = (transaction) => {
  return convertTransactionDateTime(transaction, 'toUTC');
};

/**
 * Convert array of transactions date/time fields from UTC to local time
 * @param {Array} transactions - Array of transaction objects
 * @returns {Array} - Array of transactions with converted date/time fields
 */
export const fConvertTransactionsToLocal = (transactions) => {
  return convertTransactionsDateTime(transactions, 'toLocal');
};

/**
 * Convert array of transactions date/time fields from local to UTC time
 * @param {Array} transactions - Array of transaction objects
 * @returns {Array} - Array of transactions with converted date/time fields
 */
export const fConvertTransactionsToUTC = (transactions) => {
  return convertTransactionsDateTime(transactions, 'toUTC');
};

/**
 * Check if a transaction is Razorpay transaction (skip date/time conversion)
 * @param {Object} transaction - Transaction object
 * @returns {boolean} - True if Razorpay transaction
 */
export const fIsRazorpayTransaction = (transaction) => {
  return isRazorpayTransaction(transaction);
};

/**
 * Format date/time for charger logs, converting from UTC to local time
 * @param {string} utcDateTime - UTC date/time string from backend
 * @returns {string} - Formatted local time string
 */
export const fChargerLogDateTime = (utcDateTime) => {
  if (!utcDateTime) return '-';

  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';

  return format(localDate, "yyyy-MM-dd hh:mm:ss a");
};

/**
 * Format a UTC timestamp in an explicit IANA timezone, with the zone abbreviation
 * appended (e.g. "2025-09-24 05:33:18 am GMT+5:30"). Unlike date-fns `format`, this
 * renders the instant in ANY timezone, not just the browser's, so the logs view can
 * offer a timezone selector. Falls back to browser-local formatting on a bad zone.
 *
 * @param {string} utcDateTime - UTC date/time string from backend
 * @param {string} timeZone    - IANA zone id (e.g. "Asia/Kolkata", "UTC")
 * @returns {string} - Formatted time string with zone label
 */
export const fLogDateTimeInZone = (utcDateTime, timeZone) => {
  if (!utcDateTime) return '-';

  const date = convertUTCToLocal(utcDateTime);
  if (!date) return '-';

  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(date);
  } catch (e) {
    // Invalid/unsupported timeZone -> fall back to browser-local formatting.
    return fChargerLogDateTime(utcDateTime);
  }
};

/**
 * Format date/time for general display with timezone awareness
 * @param {string} utcDateTime - UTC date/time string from backend
 * @param {string} format - Format string (default: "dd MMM yyyy HH:mm")
 * @returns {string} - Formatted local time string
 */
export const fDisplayDateTime = (utcDateTime, format = "dd MMM yyyy HH:mm") => {
  if (!utcDateTime) return '-';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';
  
  return format(localDate, format);
};

/**
 * Format date only with timezone awareness
 * @param {string} utcDateTime - UTC date/time string from backend
 * @returns {string} - Formatted local date string
 */
export const fDisplayDate = (utcDateTime) => {
  if (!utcDateTime) return '-';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';
  
  return format(localDate, "dd MMM yyyy");
};

/**
 * Format time only with timezone awareness
 * @param {string} utcDateTime - UTC date/time string from backend
 * @returns {string} - Formatted local time string
 */
export const fDisplayTime = (utcDateTime) => {
  if (!utcDateTime) return '-';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';
  
  return format(localDate, "HH:mm:ss");
};

// =============================================================================
// API SUBMISSION UTILITIES (Local → UTC)
// =============================================================================

/**
 * Convert form date/time input to UTC for API submission
 * @param {Date|string} localDateTime - Local date/time from form
 * @returns {string} - UTC ISO string for API
 */
export const fFormToUTC = (localDateTime) => {
  return convertLocalToUTC(localDateTime);
};

/**
 * Convert form date/time input to UTC with specific format for API
 * @param {Date|string} localDateTime - Local date/time from form
 * @param {string} format - Format string (default: ISO)
 * @returns {string} - Formatted UTC string for API
 */
export const fFormToUTCFormatted = (localDateTime, format = 'iso') => {
  const utcDate = convertLocalToUTC(localDateTime);
  if (!utcDate) return '';
  
  switch (format) {
    case 'iso':
      return utcDate;
    case 'datetime':
      return format(new Date(utcDate), "yyyy-MM-dd HH:mm:ss");
    case 'date':
      return format(new Date(utcDate), "yyyy-MM-dd");
    case 'time':
      return format(new Date(utcDate), "HH:mm:ss");
    default:
      return utcDate;
  }
};

/**
 * Convert entire form data object from local to UTC for API submission
 * @param {Object} formData - Form data object
 * @param {Array} dateTimeFields - Array of field names that contain date/time
 * @returns {Object} - Form data with UTC date/time fields
 */
export const fConvertFormDataToUTC = (formData, dateTimeFields = []) => {
  if (!formData || !Array.isArray(dateTimeFields)) return formData;
  
  const convertedData = { ...formData };
  
  dateTimeFields.forEach(field => {
    if (convertedData[field]) {
      convertedData[field] = convertLocalToUTC(convertedData[field]);
    }
  });
  
  return convertedData;
};

/**
 * Get current date/time as UTC for API submission
 * @returns {string} - Current UTC ISO string
 */
export const fGetCurrentUTC = () => {
  return getCurrentUTC();
};

// Re-export the utility functions for convenience
export {
  convertLocalToUTC,
  convertUTCToLocal,
  formatUTCToLocal,
  getCurrentUTC,
  isRazorpayTransaction,
  convertTransactionDateTime,
  convertTransactionsDateTime
};
